import React, { useCallback, useEffect, useState } from "react";
import { useNotify } from "react-admin";
import { useField } from "react-final-form";
import { isNumber, sortBy } from "lodash";
import ReactDataGrid from "react-data-grid";

interface ISubcontractorErpDatagrid {
  isDisabled?: boolean;
}

interface IRow {
  id: number;
  schoolId: number;
  routeId: number;
  routeName: string;
  amount: number;
  remark: string;
}

interface ISubcontractorErpInsert {
  id: number;
  schoolId: number;
  routeId: number;
  routeName: string;
  amount: number;
  remark: string;
}

const PriceFormatter = ({ value }: any) => {
  return (
    <span>
      {Number(Math.round((Number(value) + Number.EPSILON) * 100) / 100)}
    </span>
  );
};

// Formatter for the "remark" column to allow multiline text display
const RemarkFormatter = ({ value }: any) => {
  return (
    <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {value}
    </span>
  );
};

const columns = [
  { key: "routeName", name: "Route Name", width: 200 },
  {
    key: "amount",
    name: "Amount",
    editable: true,
    width: 120,
    formatter: PriceFormatter,
  },
  {
    key: "remark",
    name: "Remark",
    editable: true,
    formatter: RemarkFormatter,
  },
];

const SubcontractorErpDatagrid: React.FC<ISubcontractorErpDatagrid> = ({
  isDisabled,
}) => {
  const [rows, setRows] = useState<IRow[] | []>([]);
  const [isLoaded, setLoaded] = useState(false);
  const notify = useNotify();

  //only used for update case
  const subcontractorErpItems = useField("subcontractorErpItems");
  const itemsToSend = useField("itemsToSend");

  // ERP items values only for first time if it is not undefined
  useEffect(() => {
    if (!isLoaded && subcontractorErpItems.input.value !== "") {
      let rowData: IRow[] = [];

      subcontractorErpItems.input.value.forEach((item: any) => {
        const data: IRow = {
          id: item.id,
          schoolId: item.schoolId,
          routeId: item.routeId,
          routeName: item.routeName,
          amount: item.amount / 100,
          remark: item.remark,
        };
        rowData.push(data);
      });

      rowData = sortBy(rowData, [
        (o: any) => {
          return o.routeName;
        },
      ]);

      setRows(rowData);
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcontractorErpItems.input.value]);

  //update react-form data provider if there is a change in rows
  useEffect(() => {
    const insertData: ISubcontractorErpInsert[] = [];

    rows.forEach((rowItem: IRow) => {
      insertData.push({
        id: rowItem.id,
        schoolId: rowItem.schoolId,
        routeId: rowItem.routeId,
        routeName: rowItem.routeName,
        amount: Math.round(Number(rowItem.amount) * 100),
        remark: rowItem.remark,
      });
    });

    itemsToSend.input.onChange(insertData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const onGridRowsUpdated = useCallback(
    ({ fromRow, toRow, updated }: any) => {
      const updatedRows = rows.slice();

      if (
        updated.hasOwnProperty("amount") &&
        !isNumber(Number(updated.amount))
      ) {
        return notify(`Please input Number`, "warning");
      }

      for (let i = fromRow; i <= toRow; i++) {
        updatedRows[i] = { ...updatedRows[i], ...updated };
      }

      setRows(updatedRows);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setRows, rows],
  );

  if (rows.length === 0) {
    return <h4>Loading...</h4>;
  } else {
    return (
      <ReactDataGrid
        columns={columns}
        rowGetter={(i) => rows[i]}
        rowsCount={rows.length}
        minHeight={400}
        rowHeight={60}
        enableCellSelect={!isDisabled}
        onGridRowsUpdated={onGridRowsUpdated}
      />
    );
  }
};

export default SubcontractorErpDatagrid;
